import type { FC } from 'react';
import { memo } from 'react';

import {
  DangerButton,
  Divider,
  type FilterTag,
  FilterTags,
  Flex,
  Form,
  PrimaryButton,
  Spacer,
  TertiaryButton,
} from '@procurify/ui'
import { type FormikHelpers as FormikActions } from 'formik'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import { type CreateSavedFilterSchema } from '@webapp/api/SavedFilterAPI'
import { type SavedFilterCollection } from '@webapp/hooks/useSavedFilterManager/types'
import { type SavedFilter } from '@webapp/models/SavedFilter'
import messages from './messages'
import { SavedFiltersFormStyled } from './styles/SavedFiltersFormStyled'
import { type FilterManagerFilterTag } from '../FilterManager/types'

export interface ISavedFiltersFormProps {
  collection?: SavedFilterCollection
  savedFilter?: SavedFilter
  filters?: FilterManagerFilterTag[]
  onSubmit?: (
    data: CreateSavedFilterSchema,
    actions: FormikActions<SavedFiltersFormFormSchema>
  ) => void
  onCancel?: () => void
  onDelete?: () => void
}

export interface SavedFiltersFormFormSchema {
  name: string
}

export const formatFilterTagForSavedFilter = (
  filterTags: FilterManagerFilterTag[]
): SavedFilter['filters'] => {
  return filterTags.reduce((prev, curr) => {
    prev[curr.key] = {
      id: curr.paramValue,
      name: curr.label,
    }

    return prev
  }, {})
}

export const formatSavedFiltersToFilterTag = (
  filters: SavedFilter['filters']
): FilterTag[] => {
  const formattedFilters = []

  Object.keys(filters).forEach((filterKey) => {
    formattedFilters.push({
      key: filterKey,
      label: filters[filterKey].name,
      value: filters[filterKey].id,
      tooltip: filterKey,
    })
  })

  return formattedFilters
}

export const SavedFiltersForm: FC<ISavedFiltersFormProps> = memo(
  (props) => {
    const { collection, filters, savedFilter, onSubmit, onCancel, onDelete } =
      props

    const intl = useIntl()

    const validationSchema = Yup.object<SavedFiltersFormFormSchema>({
      name: Yup.string().required(
        intl.formatMessage(messages.nameRequiredError)
      ),
    })

    const initialFormValues = {
      name: savedFilter ? savedFilter.name : '',
    }

    const handleSubmit = ({ name }, actions) => {
      onSubmit(
        {
          name,
          filters: formatFilterTagForSavedFilter(filters),
          collection,
        },
        actions
      )
    }

    const canDelete = savedFilter && savedFilter.id ? true : false

    const filterTagPreview =
      filters?.length > 0
        ? filters
        : savedFilter
          ? formatSavedFiltersToFilterTag(savedFilter.filters)
          : []

    return (
      <SavedFiltersFormStyled>
        <Form<SavedFiltersFormFormSchema>
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form.Content noValidate autoComplete='off'>
              <Flex spacing={0}>
                <Flex.Row flexWrap='wrap' spacing={1}>
                  <Flex.Item flex='1 0'>
                    <Form.TextField
                      id='name'
                      name='name'
                      placeholder={intl.formatMessage(
                        messages.nameFieldPlaceholder
                      )}
                      label={intl.formatMessage(messages.nameFieldLabel)}
                      required
                    />
                  </Flex.Item>
                </Flex.Row>
              </Flex>

              {filterTagPreview.length > 0 && (
                <>
                  <Spacer space={1} />
                  <FilterTags
                    tags={filterTagPreview}
                    readonly
                    canClear={false}
                    fullWidthTags
                  />
                </>
              )}

              <Divider space={1.5} />

              <Flex.Row>
                <Flex.Item flexGrow={1}>
                  {canDelete && (
                    <DangerButton
                      type='button'
                      onClick={onDelete}
                      disabled={isSubmitting}
                    >
                      {intl.formatMessage(messages.deleteFilterLabel)}
                    </DangerButton>
                  )}
                </Flex.Item>
                <Flex.Item>
                  <TertiaryButton
                    type='button'
                    disabled={isSubmitting}
                    onClick={onCancel}
                  >
                    {intl.formatMessage(messages.cancelButton)}
                  </TertiaryButton>
                  <PrimaryButton type='submit' isLoading={isSubmitting}>
                    {intl.formatMessage(messages.submitButton)}
                  </PrimaryButton>
                </Flex.Item>
              </Flex.Row>
            </Form.Content>
          )}
        </Form>
      </SavedFiltersFormStyled>
    )
  }
)

SavedFiltersForm.defaultProps = {
  filters: [],
}

SavedFiltersForm.displayName = 'SavedFiltersForm'
